import Badge from '@mui/material/Badge';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { checkPlaceholders, placeholderToken } from '@/models/skin/ContentString';

interface Props {
    /** Locales to offer, in display order. */
    locales: string[];
    /** Current values, keyed by locale exactly as `locales` spells them. */
    values: Record<string, string>;
    onChange: (locale: string, value: string) => void;
    /** Read-only reference text shown under the field, keyed lowercase. */
    fallbacks?: Record<string, string>;
    fallbackLabel?: string;
    noFallbackText?: string;
    emptyPlaceholder?: string;
    disabled?: boolean;
    /** When given, the ACTIVE language gets a remove control - so it is always clear which one goes. */
    onRemoveLocale?: (locale: string) => void;
    /** Which locales may be removed. Omit to allow all. */
    canRemoveLocale?: (locale: string) => boolean;
    removeLabel?: string;
    /** Tokens this key declares, bare. Rendered as chips that insert at the cursor. */
    placeholders?: string[];
    placeholderLabel?: string;
    /** Contains a {{tokens}} placeholder for the offending list. */
    unknownPlaceholderText?: string;
    /** Contains a {{tokens}} placeholder for the unused list. */
    missingPlaceholderText?: string;
}

/**
 * Editing one string across many languages: a tab per locale, one field at a time.
 *
 * <p>Stacking a field per language does not survive contact with reality - a brand operating in thirty
 * markets would get a thirty-field wall, and the dialog becomes a scroll hunt. One field at a time keeps the
 * surface constant no matter how many languages exist, and scrollable tabs absorb the growth sideways.</p>
 *
 * <p>The dot on a tab marks a language that HAS text. Without it, finding which of thirty languages are still
 * empty means clicking all thirty - which is exactly the job someone opens this to do.</p>
 */
export default function LocaleValueEditor({
    locales, values, onChange, fallbacks, fallbackLabel, noFallbackText, emptyPlaceholder, disabled,
    onRemoveLocale, canRemoveLocale, removeLabel,
    placeholders, placeholderLabel, unknownPlaceholderText, missingPlaceholderText,
}: Props) {
    const [active, setActive] = useState(locales[0] ?? '');
    const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

    // Keep the selection valid when the locale set changes underneath - adding a language in the admin
    // dialog, or switching to a brand with a different set.
    useEffect(() => {
        if (locales.length > 0 && !locales.includes(active)) setActive(locales[0]);
    }, [locales, active]);

    if (locales.length === 0) return null;

    const fallback = fallbacks?.[active.toLowerCase()] ?? '';
    const current = values[active] ?? '';

    /**
     * Puts a token where the caret is, not on the end.
     *
     * The token belongs mid-sentence, so appending would make every use a cut-and-paste job. Typing it by
     * hand is worse: "#point#" and "#Points#" both look right and neither is ever replaced.
     */
    const insertPlaceholder = (name: string) => {
        const token = placeholderToken(name);
        const el = inputRef.current;

        if (!el) {
            onChange(active, current + token);
            return;
        }

        const start = el.selectionStart ?? current.length;
        const end = el.selectionEnd ?? start;
        onChange(active, current.slice(0, start) + token + current.slice(end));

        // After the re-render, or the caret jumps to the end and the next click lands in the wrong place.
        requestAnimationFrame(() => {
            const caret = start + token.length;
            el.focus();
            el.setSelectionRange(caret, caret);
        });
    };

    // Checked against the ACTIVE language only. A brand part-way through translating has empty fields, and
    // reporting every untranslated language as "missing" would bury the one real problem.
    const issues = placeholders?.length ? checkPlaceholders(current, placeholders) : null;
    const showMissing = !!issues && current.trim().length > 0 && issues.missing.length > 0;

    return (
        <Box>
            <Tabs
                value={locales.includes(active) ? active : locales[0]}
                onChange={(_, v) => setActive(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ minHeight: 36, mb: 1, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
            >
                {locales.map(locale => {
                    const hasValue = (values[locale] ?? '').trim().length > 0;
                    return (
                        <Tab
                            key={locale}
                            value={locale}
                            label={
                                <Badge
                                    variant="dot"
                                    color="primary"
                                    invisible={!hasValue}
                                    sx={{ '& .MuiBadge-badge': { right: -8, top: 4 } }}
                                >
                                    {locale}
                                </Badge>
                            }
                        />
                    );
                })}
            </Tabs>

            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label={active}
                    value={current}
                    disabled={disabled}
                    onChange={e => onChange(active, e.target.value)}
                    placeholder={fallback || emptyPlaceholder}
                    inputRef={inputRef}
                    error={!!issues?.unknown.length}
                />
                {onRemoveLocale && (canRemoveLocale?.(active) ?? true) && (
                    <Tooltip title={removeLabel ?? ''}>
                        <IconButton onClick={() => onRemoveLocale(active)} disabled={disabled}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                </Stack>

                {!!placeholders?.length && (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {placeholderLabel && (
                            <Typography variant="caption" color="text.secondary">{placeholderLabel}</Typography>
                        )}
                        {placeholders.map(name => (
                            <Chip
                                key={name}
                                size="small"
                                variant="outlined"
                                label={placeholderToken(name)}
                                onClick={() => insertPlaceholder(name)}
                                disabled={disabled}
                            />
                        ))}
                    </Stack>
                )}

                {/* An unknown token is an error: it renders literally to a customer and nobody finds out until
                    they ask. A declared token the client chose not to use is only a note - text that does not
                    mention the number is legitimate wording, and nagging would be overruling them on their
                    own copy. */}
                {!!issues?.unknown.length && unknownPlaceholderText && (
                    <Typography variant="caption" color="error">
                        {unknownPlaceholderText.replace(
                            '{{tokens}}', issues.unknown.map(placeholderToken).join(', '))}
                    </Typography>
                )}
                {showMissing && missingPlaceholderText && (
                    <Typography variant="caption" color="text.secondary">
                        {missingPlaceholderText.replace(
                            '{{tokens}}', issues.missing.map(placeholderToken).join(', '))}
                    </Typography>
                )}

                {fallbackLabel && (
                    <Typography variant="caption" color="text.secondary">
                        {fallback ? `${fallbackLabel}: ${fallback}` : noFallbackText}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
}
